import {
  useState,
  useEffect,
  Activity,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Button,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Tabs,
  TabsList,
  TabsTrigger,
  V2Dialog,
  VisuallyHidden,
} from "web-design-system";
import {
  NotebookPen,
  CalendarIcon,
  PencilLine,
  XIcon,
  Pause,
  Play,
  MicIcon,
  CircleAlert,
  SquareIcon,
  CircleAlertIcon,
  PenLineIcon,
} from "lucide-react";
import { motion } from "motion/react";
import { useNotes, useSaveNote, useUpdateNote } from "creso-core/hooks";
import type { NoteDetail, NoteListItem } from "creso-core/types";
import { Client } from "creso-core/types";
import { useAudioTranscription } from "@core/hooks/use-audio-transcribe";
import { useMicrophonePermission } from "@core/hooks/use-microphone-permission";
import { handleError } from "@/utils/error";
import { usePrevious } from "@funds/pages/funds-list";
import { sleep } from "creso-core/utils";
import { ConditionalRenderer } from "../core/components/conditional-renderer";
import { NotebookContext, View } from "./notebook-context";
import { NoteSection } from "./note-section";
import { NotesList } from "./notes-list";
import { TaskCalendar } from "./task-calendar";
import { TaskSection } from "./tasks-section";
import {
  transformNoteDetailToListItem,
  transformNoteListToNoteDetailItem,
} from "./utils";

const draftNoteGenerator = (
  title: string,
  client: Client | null
): NoteListItem => ({
  id: -1,
  title,
  raw_text: "",
  created_at: new Date().toISOString(),
  task_count: 0,
  summary: "",
  client_id: client?.id,
  client_name: client?.name,
});

export type RecordingStatus =
  | "idle"
  | "recording"
  | "paused"
  | "analysing"
  | "creating-todos";

type NoteBookDialogProps = {
  trigger: (props: { onClick: () => void; open: boolean }) => React.ReactNode;
  view?: View;
  action?: "record-note" | "add-note" | "add-task";
  client?: Client;
  clientIds?: number[];
  note?: NoteListItem;
};

const MAX_RECORDING_SECONDS = 60;

export function NotebookDialog(props: NoteBookDialogProps) {
  const [view, setView] = useState<View>(props.view ?? "notes");
  const [selectedNote, setSelectedNote] = useState<NoteListItem | null>(
    props.note ?? null
  );

  const previousNote = usePrevious(selectedNote);

  const [open, setOpen] = useState(false);

  const { notes: allNotes } = useNotes();

  const hasNotes = useMemo(() => allNotes && !!allNotes.length, [allNotes]);

  const [filters, setFilters] = useState<{
    date: Date;
    client: Client | null;
  }>({
    client: props.clientIds ? null : (props.client ?? null),
    date: new Date(),
  });

  useEffect(() => {
    if (open) {
      setSelectedNote(props.note ?? null);
    } else {
      setSelectedNote(null);
    }
  }, [props.note, open]);

  useEffect(() => {
    setFilters((f) => ({
      ...f,
      client: props.clientIds ? null : (props.client ?? null),
    }));
  }, [props.client, props.clientIds, open]);

  const [elapsed, setElapsed] = useState(0);

  const { start, stop, transcript } = useAudioTranscription();
  const { status: micPermissionStatus, requestPermission } =
    useMicrophonePermission();
  const saveNoteMutation = useSaveNote();

  const [recordingStatus, setRecordingStatus] =
    useState<RecordingStatus>("idle");

  useEffect(() => {
    if (!transcript) return;
    setSelectedNote((prev) => {
      if (!prev || prev.id !== -1) return prev;
      return { ...prev, raw_text: transcript, summary: transcript };
    });
  }, [transcript]);

  const handleStopRecording = async () => {
    stop();
    if (!selectedNote?.raw_text) {
      setRecordingStatus("idle");
      setSelectedNote(
        previousNote && previousNote?.id !== -1 ? previousNote : null
      );
      return;
    }
    await saveNote(transformNoteListToNoteDetailItem(selectedNote));
  };

  const saveRecordingRef = useRef(handleStopRecording);
  saveRecordingRef.current = handleStopRecording;

  useEffect(() => {
    if (recordingStatus !== "recording") return;
    const interval = setInterval(() => {
      setElapsed((e) => {
        if (e >= MAX_RECORDING_SECONDS - 1) {
          saveRecordingRef.current();
          return e;
        }
        return e + 1;
      });
    }, 1000);
    // eslint-disable-next-line
    return () => clearInterval(interval);
  }, [recordingStatus]);

  const updateNoteMutation = useUpdateNote();

  const handleDictateClick = useCallback(async () => {
    setView("notes");
    setSelectedNote(draftNoteGenerator("Typing...", filters.client));
    if (micPermissionStatus !== "granted") {
      const granted = await Promise.race([requestPermission(), sleep(20000)]);
      if (!granted) return;
    }
    setSelectedNote(draftNoteGenerator("Listening...", filters.client));
    setRecordingStatus("recording");
    await start();
    setElapsed(0);
  }, [start, micPermissionStatus, requestPermission, filters]);

  const startDictationRef = useRef(handleDictateClick);

  startDictationRef.current = handleDictateClick;

  const handlePause = useCallback(() => {
    setRecordingStatus("paused");
    stop();
  }, [stop]);

  const handleResume = useCallback(() => {
    setRecordingStatus("recording");
    start();
  }, [start]);

  const exitDictate = useCallback(() => {
    stop();
    setRecordingStatus("idle");
    setSelectedNote(null);
    setElapsed(0);
  }, [stop]);

  const exitDictateRef = useRef(exitDictate);

  const handleNewNoteClick = () => {
    setView("notes");
    setSelectedNote(draftNoteGenerator("Typing...", filters.client));
  };

  const { trigger: Trigger } = props;

  const startRecording = props.action === "record-note";

  useEffect(() => {
    if (open) {
      if (startRecording) startDictationRef.current();
    } else {
      exitDictateRef.current();
    }
  }, [startRecording, open]);

  const reset = useCallback(
    (note?: NoteListItem, clear?: boolean) => {
      if (recordingStatus !== "idle") {
        exitDictate();
      }
      // A note can be passed when it is newly add
      // If no note is passed (interupt adding, editing, dictation etc)
      // try and select the previously selected note.
      // If previously selected note doesn't exist or if it was a draft note
      // that got discarded, set it to null
      // null will force NoteList to auto-select the first note
      setSelectedNote(
        clear
          ? null
          : (note ??
              (previousNote && previousNote.id !== -1 ? previousNote : null))
      );
    },
    [recordingStatus, exitDictate, previousNote]
  );

  const [isSavingNote, setIsSavingNote] = useState(false);

  const saveNote = useCallback(
    async (note: NoteDetail) => {
      try {
        setIsSavingNote(true);
        if (note.id && note.id !== -1) {
          const updateNote = await updateNoteMutation.mutateAsync({
            clientId: note.client_id,
            summary: note.summary,
            id: note.id,
          });
          reset(transformNoteDetailToListItem(updateNote));
        } else {
          try {
            setRecordingStatus("analysing");
            await new Promise<void>((r) => {
              setTimeout(() => r(), 1000);
            });
            setRecordingStatus("creating-todos");
            await new Promise<void>((r) => {
              setTimeout(() => r(), 500);
            });
            const savedNote = await saveNoteMutation.mutateAsync({
              clientId: note.client_id ?? undefined,
              text: note.summary,
            });
            reset(savedNote.note);
            setRecordingStatus("idle");
          } catch (error) {
            handleError(error, "Failed to add note");
          }
        }
      } finally {
        setIsSavingNote(false);
      }
    },
    [saveNoteMutation, reset, updateNoteMutation]
  );

  const notebookContextValue = useMemo(
    () => ({
      selectedNote,
      setSelectedNote,
      filters,
      setFilters,
      recordingStatus,
      reset,
      saveNote,
      view,
      setView,
      clientIds: props.clientIds,
    }),
    [
      selectedNote,
      filters,
      recordingStatus,
      reset,
      saveNote,
      view,
      setView,
      props.clientIds,
    ]
  );

  return (
    <V2Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {Trigger ? <Trigger onClick={() => setOpen(true)} open={open} /> : null}
      </DialogTrigger>
      <DialogContent className="w-220 max-w-none bg-card h-170 p-4 gap-4 overflow-visible flex flex-col">
        <NotebookContext.Provider value={notebookContextValue}>
          <VisuallyHidden>
            <DialogTitle>Notebook</DialogTitle>
          </VisuallyHidden>

          {/* Header */}
          <div className="flex h-9 items-center gap-4 justify-between shrink-0">
            <span className="text-lg w-70 font-semibold">Notebook</span>
            <div className="flex-1 flex gap-3 items-center">
              <Activity mode={selectedNote?.id !== -1 ? "visible" : "hidden"}>
                <Tabs
                  value={view}
                  onValueChange={(v) => {
                    exitDictate();
                    setView(v as View);
                  }}
                >
                  <TabsList className="bg-secondary h-9">
                    <TabsTrigger value="notes">
                      <NotebookPen className="size-4" />
                    </TabsTrigger>
                    <TabsTrigger value="calendar">
                      <CalendarIcon className="size-4" />
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <Button
                  size="icon"
                  className="ml-auto"
                  variant="outline"
                  onClick={handleNewNoteClick}
                >
                  <PencilLine />
                </Button>
                <div className="relative h-9 flex items-center bg-secondary rounded-lg">
                  <span className="px-3 font-semibold text-sm">Add Note</span>
                  <Button
                    size="icon"
                    onClick={handleDictateClick}
                    disabled={recordingStatus !== "idle"}
                  >
                    <MicIcon />
                  </Button>
                  <ConditionalRenderer
                    condition={micPermissionStatus !== "granted"}
                  >
                    <span
                      title="Microphone permission required"
                      className="absolute -top-2 -right-2 flex size-4 items-center justify-center rounded-full bg-card pointer-events-none"
                    >
                      <CircleAlert className="size-4 text-tag-yellow-foreground" />
                    </span>
                  </ConditionalRenderer>
                </div>
              </Activity>
              <Activity mode={selectedNote?.id === -1 ? "visible" : "hidden"}>
                <ConditionalRenderer condition={recordingStatus === "idle"}>
                  <div className="flex items-center gap-2 w-full">
                    <Button
                      onClick={() => reset(undefined, previousNote?.id === -1)}
                      variant="outline"
                      size="icon"
                    >
                      <XIcon />
                    </Button>
                    <span className="text-sm font-semibold">New Note</span>
                    <Button
                      onClick={() => {
                        if (selectedNote)
                          saveNote(
                            transformNoteListToNoteDetailItem(selectedNote)
                          );
                      }}
                      className="ml-auto"
                      disabled={!selectedNote?.summary || isSavingNote}
                      variant="outline"
                    >
                      <PenLineIcon />
                      Save Note
                    </Button>
                    <div className="relative">
                      <Button
                        disabled={isSavingNote}
                        onClick={handleDictateClick}
                        size="icon"
                      >
                        <MicIcon />
                      </Button>
                      {micPermissionStatus !== "granted" && (
                        <span
                          title="Microphone permission required"
                          className="absolute -top-2 -right-2 flex size-4 items-center justify-center rounded-full bg-card pointer-events-none"
                        >
                          <CircleAlert className="size-4 text-tag-yellow-foreground" />
                        </span>
                      )}
                    </div>
                  </div>
                </ConditionalRenderer>
                <ConditionalRenderer
                  condition={
                    recordingStatus === "recording" ||
                    recordingStatus === "paused"
                  }
                >
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="flex flex-row h-9 pr-15 items-center gap-3 ml-auto overflow-hidden"
                  >
                    <div className="flex items-center h-9 bg-linear-to-r border border-border shadow-xs from-background to-pink-100  rounded-lg px-1 gap-1 flex-1 min-w-0">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 shrink-0"
                        onClick={exitDictate}
                      >
                        <XIcon className="size-3.5" />
                      </Button>
                      <span className="text-xs font-medium tabular-nums w-9 shrink-0 text-center">
                        {`${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`}
                      </span>
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-destructive rounded-full transition-all duration-1000 ease-linear"
                          style={{
                            width: `${(elapsed / MAX_RECORDING_SECONDS) * 100}%`,
                          }}
                        />
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 shrink-0"
                        onClick={
                          recordingStatus === "paused"
                            ? handleResume
                            : handlePause
                        }
                      >
                        {recordingStatus === "paused" ? (
                          <Play className="size-3.5" />
                        ) : (
                          <Pause className="size-3.5" />
                        )}
                      </Button>
                    </div>
                    <button
                      onClick={handleStopRecording}
                      className="rounded-full active:translate-y-px shadow absolute right-4 flex items-center justify-center size-12 bg-name-tag-rose-foreground"
                    >
                      <SquareIcon className="size-4 stroke-background fill-background" />
                    </button>
                  </motion.div>
                </ConditionalRenderer>
              </Activity>
            </div>
          </div>

          <div className="flex flex-1 gap-4 overflow-hidden">
            <div className="w-70 shrink-0 flex flex-col overflow-auto">
              <Activity mode={view === "notes" ? "visible" : "hidden"}>
                <NotesList />
              </Activity>
              <Activity mode={view === "calendar" ? "visible" : "hidden"}>
                <TaskCalendar />
              </Activity>
            </div>

            <Activity mode={view === "notes" ? "visible" : "hidden"}>
              <div className="flex flex-col flex-1 gap-4">
                {micPermissionStatus === "denied" && (
                  <div className="bg-tag-yellow h-9 px-3 py-2 gap-2 flex items-center rounded-lg border border-border text-tag-yellow-foreground">
                    <CircleAlertIcon className="size-4" />
                    <span className="text-xs">
                      Microphone access is blocked. Enable it in your browser
                      settings to dictate notes.
                    </span>
                  </div>
                )}
                <NoteSection showEmpty={!hasNotes} />
              </div>
            </Activity>
            <Activity mode={view === "calendar" ? "visible" : "hidden"}>
              <TaskSection initialIsAdding={props.action === "add-task"} />
            </Activity>
          </div>
        </NotebookContext.Provider>
      </DialogContent>
    </V2Dialog>
  );
}
